import React, { useRef, useEffect } from 'react';
import './CalculusOfMeaning.css';

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

function ChemistryOfLeadership() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Voice &middot; Frequency &middot; The Hertz of Trust</span>
          <h1>The Chemistry of Leadership</h1>
          <p className="calculus-hero-sub">A leader who cannot hear the cortisol in someone's voice will never know they lost them until the resignation letter arrives.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <div className="calculus-content">

          {/* a colleague */}
          <section className="calc-section reveal">
            <h2>The Eleven-Second Intervention</h2>
            <p className="calc-lead">
              He had just lost his job. He was building a slide deck, something he could contribute,
              something that was his. When someone suggested that AI could enhance his presentation
              by embedding a live demo into a web page, his voice changed.
            </p>
            <p>
              "Oh, well if the AI can do it, I don't need to."
            </p>
            <p>
              The words sounded casual. The frequency did not. Somewhere between "well" and "need,"
              the pitch tightened. The pace compressed. The breath shortened. His nervous system had
              processed the suggestion not as collaboration but as replacement. The cortisol was
              already rising before the sentence ended.
            </p>
            <p>
              The person listening heard it. Not the words. The hertz.
            </p>
            <p>
              "a colleague. I'm not trying to take away your contribution."
            </p>
            <p>
              He tried to justify his response. Tried to cover the frequency shift with logic. She
              stopped him again.
            </p>
            <p>
              "No. You matter. Your ideas matter. A machine without us is just a machine."
            </p>
            <p>
              He went quiet. Then he heard it. And he said, "Well, look at you going and being the
              good HR person."
            </p>
            <p>
              It was not HR. It was neurochemistry. She heard the cortisol spike in his voice before
              he could name it. She identified the threat response (I am being replaced) before it
              could calcify into belief. And she administered the counter-signal (you matter) before
              the scar could form.
            </p>
            <p>
              That interaction took eleven seconds. It saved a working relationship, a man's sense
              of professional worth, and a collaboration that went on to produce something neither
              of them could have built alone.
            </p>
            <p>
              Eleven seconds. Because someone listened to the hertz, not the words.
            </p>
          </section>

          <Pulse />

          {/* What Leaders Are Trained to Hear */}
          <section className="calc-section reveal">
            <h2>What Leaders Are Trained to Hear</h2>
            <p>
              Leadership training teaches words. Active listening frameworks. Empathy scripts.
              "I hear what you're saying." "Help me understand." "That must be frustrating."
            </p>
            <p>
              All of it operates at the content layer. What did the person say? What is the
              appropriate verbal response? How do I acknowledge their position while redirecting
              toward the organizational objective?
            </p>
            <p>
              None of it operates at the frequency layer. How did their voice change when they said
              it? Did the pitch rise or fall? Did the pace compress or expand? Did the pauses get
              shorter (cortisol, urgency, threat) or longer (withdrawal, shutdown, resignation)?
            </p>

            <div className="calc-table-wrap">
              <table className="calc-table">
                <thead>
                  <tr>
                    <th>What Leaders Are Trained to Hear</th>
                    <th>What Actually Carries the Information</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>The content of the sentence</td><td>The frequency profile of the voice</td></tr>
                  <tr><td>"I'm fine"</td><td>Pitch flat, pace slow, breath shallow = not fine</td></tr>
                  <tr><td>"That's a great idea"</td><td>Rising terminal pitch, compressed pause = feels threatened by the idea</td></tr>
                  <tr><td>"I can handle it"</td><td>Tight vocal cords, reduced dynamic range = already past capacity</td></tr>
                  <tr><td>"No problem"</td><td>Forced cadence, pitch drop at "problem" = it is a problem</td></tr>
                </tbody>
              </table>
            </div>

            <p>
              Every leader has heard "I'm fine" from someone who was not fine. Every leader has
              sensed that something was off without being able to articulate what. That sense is not
              intuition. It is the leader's own nervous system detecting a frequency mismatch between
              the words and the voice producing them. The content said one thing. The hertz said
              another. The leader's amygdala caught the discrepancy but the training gave them no
              framework for acting on it.
            </p>

            <div className="calc-thesis-box">
              <p>Leadership training that operates only at the content layer will reliably miss the signals that matter most. The employee who is burning out does not announce it in words. They announce it in hertz. The frequency changes weeks before the words do.</p>
            </div>
          </section>

          <Pulse />

          {/* The Chemistry of the Lost Hallway */}
          <section className="calc-section reveal">
            <h2>The Chemistry of the Lost Hallway</h2>
            <p>
              When the pandemic moved work behind screens, the industry measured what was lost in
              terms of productivity, collaboration, and culture. What was actually lost was
              neurochemistry.
            </p>
            <p>
              The hallway smile triggers a micro-dose of oxytocin in both parties. The passing nod
              of recognition activates the social bonding circuit. The spontaneous "hey, how's it
              going?" in the elevator produces a brief serotonin exchange that neither person notices
              but both benefit from. These interactions are pharmacologically trivial in isolation.
              Accumulated across a workday, they constitute the chemical foundation of belonging.
            </p>
            <p>
              Remote work did not remove convenience. It removed chemistry.
            </p>
            <p>
              The "no video" call strips out 93% of nonverbal communication. The muted microphone
              eliminates the validating "mm-hmm" that tells the speaker their nervous system is safe
              to continue. The linear audio flow of video conferencing removes the overlapping
              affirmations that signal engagement. Each of these losses is small. Together, they
              constitute a systematic depletion of the oxytocin, serotonin, and dopamine exchanges
              that make a team feel like a team.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Belonging Depletion</span>
              <code>B(t) = B&#8320; &minus; &sum; [daily_OT_deficit + daily_5HT_deficit] &middot; &Delta;t</code>
              <span className="calc-eq-desc">Belonging is not a culture metric. It is a running sum of oxytocin and serotonin exchanges over time. When the daily micro-exchanges that sustain belonging are removed (hallway, elevator, spontaneous check-in), the sum trends negative. The employee does not know why they feel disconnected. Their chemistry knows.</span>
            </div>

            <p>
              One leader recognized this in 2021 and wrote that leaders must "be intentional about
              making time for chat" and that these moments "help to inform who the individual is, not
              the category or assumption we might otherwise have to place." She was prescribing
              oxytocin replacement therapy without naming it. She was right. But the prescription
              lacked the mechanism. Without understanding why the chat matters, the recommendation
              becomes another item on a wellness checklist instead of what it actually is: a
              pharmacokinetic intervention.
            </p>
          </section>

          <Pulse />

          {/* The Magnifier Effect */}
          <section className="calc-section reveal">
            <h2>The Magnifier Effect</h2>
            <p>
              "Keep in mind you're a magnifier. The energy you operate with becomes exponentially
              multiplied."
            </p>
            <p>
              This is neurochemical contagion, described in business language.
            </p>
            <p>
              When a leader enters a meeting with elevated cortisol, their voice carries a frequency
              signature that the team's nervous systems detect within milliseconds. The pitch is
              slightly higher. The pace is slightly faster. The pauses are compressed. Every person
              in the room begins a sympathetic cortisol response before the first agenda item is
              addressed. The leader's stress has become the team's stress, propagated not through
              content but through frequency.
            </p>
            <p>
              The inverse is equally true. A leader who enters with stable serotonin and elevated
              oxytocin produces a voice that is lower in pitch, steadier in pace, with longer pauses
              and downward terminal contours. The team's nervous systems read safety. Cortisol
              attenuates. Cognitive function improves. The meeting produces better decisions because
              the chemistry in the room permits better thinking.
            </p>

            <div className="calc-equation">
              <span className="calc-eq-label">Neurochemical Contagion</span>
              <code>team_cortisol(t) = f(leader_voice_frequency(t)) &middot; N_participants &middot; duration</code>
              <span className="calc-eq-desc">A leader's cortisol propagates through their vocal frequency to every participant, scaled by the number of people in the room and the duration of exposure. A stressed leader running a one-hour meeting with ten people has administered a cortisol dose to ten nervous systems for sixty minutes. The meeting's decisions will reflect the chemistry, not the agenda.</span>
            </div>

            <p>
              This is not metaphor. Mirror neuron research has established that observing someone
              in a stressed state activates the same neural circuits in the observer. Vocal frequency
              is the primary transmission channel in remote work, where visual cues are reduced or
              absent. The leader's voice IS the room's chemistry.
            </p>
          </section>

          <Pulse />

          {/* The AI Threat Response */}
          <section className="calc-section reveal">
            <h2>The AI Threat Response</h2>
            <p>
              His reaction was not about a slide deck. It was about survival.
            </p>
            <p>
              When a person's professional contribution is the primary source of their dopamine
              (agency, purpose, reward) and serotonin (status, competence, stability), a suggestion
              that AI can do part of their work triggers the same neurochemical cascade as a physical
              threat. The prefrontal cortex hears "let's enhance your presentation." The amygdala
              hears "you are becoming unnecessary."
            </p>
            <p>
              This is happening in every organization deploying AI. Every single one. And the
              leaders managing the transition are trained to address the content layer: "AI is a
              tool, not a replacement." "This will free you up for higher-value work." "Your role
              is evolving, not disappearing."
            </p>
            <p>
              The content is correct. The chemistry does not care. The employee's cortisol spiked
              at the mention of AI doing their task. The reassuring words arrive after the amygdala
              has already processed the threat. The logical explanation reaches the prefrontal cortex
              three hundred milliseconds after the survival circuit has already activated.
            </p>

            <div className="calc-thesis-box">
              <p>You cannot logic someone out of a cortisol spike. The chemistry moves faster than the words. A leader who responds to the AI threat response with content ("let me explain why this is good for you") is administering treatment to the wrong layer. The threat is not in the content. It is in the hertz. And the intervention must meet it there.</p>
            </div>

            <p>
              What he needed was not an explanation of how AI would enhance his work. What
              he needed was someone to hear the frequency shift in his voice and say, before
              the cortisol could scar: you matter. Your ideas matter. A machine without us is just
              a machine.
            </p>
            <p>
              Three sentences. Delivered at the frequency layer. Before the amygdala could finish
              its cascade.
            </p>
            <p>
              That is the chemistry of leadership.
            </p>
          </section>

          <Pulse />

          {/* What Leaders Actually Need */}
          <section className="calc-section reveal">
            <h2>What Leaders Actually Need</h2>
            <p>
              Not another empathy framework. Not another active listening checklist. Not another
              wellness webinar with five bullet points about setting boundaries and being
              intentional.
            </p>
            <p>
              Leaders need to understand what they are already hearing and do not have language for.
            </p>

            <div className="calc-implications">
              <div className="calc-implication">
                <h4>The frequency of distress.</h4>
                <p>Vocal pitch rises under cortisol. Pace compresses. Pauses shorten. Breath becomes
                shallow. These are measurable, consistent, and present weeks before the employee
                articulates a problem. A leader trained to hear frequency changes can intervene
                when the chemistry is still manageable instead of after the scar has formed.</p>
              </div>
              <div className="calc-implication">
                <h4>The frequency of withdrawal.</h4>
                <p>Dynamic range flattens. Pitch variation disappears. Responses become shorter.
                The voice loses its music. This is serotonin depletion made audible. The employee
                has not "checked out." Their chemistry has been depleted to the point where
                engagement requires more neurochemical energy than they have available.</p>
              </div>
              <div className="calc-implication">
                <h4>The frequency of threat.</h4>
                <p>Vocal cord tension increases. Pitch tightens into a narrower band. Speech becomes
                more controlled, more careful. The spontaneity disappears. This is what a colleague
                sounded like. This is what every employee sounds like when they believe their
                value is being questioned. A leader who hears it can address it in eleven seconds.
                A leader who misses it will lose the employee in eleven weeks.</p>
              </div>
              <div className="calc-implication">
                <h4>The frequency of trust.</h4>
                <p>Lower pitch. Wider dynamic range. Longer pauses without discomfort. Laughter that
                involves actual breath, not just vocalization. This is oxytocin in the voice. When
                a leader hears this, they know the relationship is safe. When they stop hearing
                it, they know something has changed, even if no one has said a word about it.</p>
              </div>
            </div>
          </section>

          <Pulse />

          {/* Every Industry. Every Role. Every Call. */}
          <section className="calc-section reveal">
            <h2>Every Industry. Every Role. Every Call.</h2>
            <p>
              This is not a healthcare framework applied to business. This is biology applied to
              every human interaction that involves a voice.
            </p>
            <p>
              The sales leader whose team is underperforming. The engineering manager whose
              senior developer has gone quiet. The CEO who can feel the room shift during an
              all-hands but cannot explain why. The teacher who knows a student is struggling
              before the grades drop. The parent who hears something in their teenager's voice
              on the phone and calls back.
            </p>
            <p>
              All of them are detecting frequency changes. All of them are processing neurochemical
              signals through their own nervous systems. None of them were trained to do it. They
              do it because biology gave them the hardware. What biology did not give them is the
              vocabulary to describe what they are hearing, or the framework to act on it
              systematically instead of instinctively.
            </p>
            <p>
              Leadership training that ignores the frequency layer is training leaders to respond
              to transcripts instead of conversations. The transcript says "I'm fine." The
              frequency says "I am drowning." The leader trained in content hears the first. The
              leader trained in chemistry hears the second.
            </p>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section calc-closing reveal">
            <p className="calc-closing-line"><em>The leadership industry trained leaders to hear words.</em></p>
            <p className="calc-closing-line"><em>The wellness industry trained leaders to ask about feelings.</em></p>
            <p className="calc-closing-line"><em>Nobody trained leaders to hear hertz.</em></p>
            <p className="calc-closing-final">That is the chemistry of leadership.<br />It was always in the voice.</p>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>AETHER: Adaptive Endocrine Transformer Heads with Emotional Regulation</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default ChemistryOfLeadership;
